import React, { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import {
  TimeWindowControl,
  DatePicker,
  Input,
  Delete,
  Warning,
  Clone,
} from 'components';
import {
  TimeWindowFormProps,
  TimeWindowHeaderWrapper,
} from 'components/TimeWindow';
import { CircleButton } from 'components/CircleButton';
import {
  CheckBoxItem,
  CheckInput,
  CheckLabel,
} from 'components/form/Check/CheckStyles';
import { Check } from 'react-bootstrap-icons';
import { ReturnComponentSearch } from 'components/Select/ReturnComponentSearch';
import { useFormErrors } from 'hooks';

export const AbsoluteReturnTimeWindow: React.FC<TimeWindowFormProps> = ({
  index,
  onClone,
  onRemove,
  refreshDates,
}) => {
  const {
    control,
    register,
    formState: { errors },
    watch,
    setValue,
    getValues,
    trigger,
    clearErrors,
  } = useFormContext();
  const fieldName = `timeWindows[${index}]`;
  const noReturnComponent = watch(`${fieldName}.noReturnComponent`);

  const { getErrorMessage, hasErrors } = useFormErrors({
    errors,
    fieldName,
    index,
  });
  const timeWindows = getValues('timeWindows');
  const [returnComponentSearch, setReturnComponentSearch] = useState('');

  useEffect(() => {
    if (noReturnComponent) {
      clearErrors(`${fieldName}.returnComponent`);
      setValue(`${fieldName}.returnComponent`, '');
      setReturnComponentSearch('');
    }
  }, [noReturnComponent]);

  return (
    <div>
      <TimeWindowHeaderWrapper key={index}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignContent: 'center',
            gap: '10px',
            flexWrap: 'wrap',
          }}
        >
          <TimeWindowControl label='From Date'>
            <Controller
              control={control}
              name={`${fieldName}.fromDate`}
              render={({ field: { onChange, value } }) => {
                return (
                  <DatePicker
                    onChange={onChange}
                    onCalendarClose={() => {
                      refreshDates?.();
                    }}
                    testid={`${fieldName}.fromDate`}
                    selected={value}
                    errorMessage={getErrorMessage(`${fieldName}.fromDate`)}
                    style={{ width: 130 }}
                  />
                );
              }}
            />
          </TimeWindowControl>
          <TimeWindowControl label='To Date'>
            <Controller
              control={control}
              name={`${fieldName}.toDate`}
              render={({ field: { onChange, value } }) => {
                return (
                  <DatePicker
                    disabled
                    testid={`${fieldName}.toDate`}
                    onChange={onChange}
                    selected={value}
                    errorMessage={getErrorMessage(`${fieldName}.toDate`)}
                    style={{ width: 130 }}
                  />
                );
              }}
            />
          </TimeWindowControl>
          <TimeWindowControl
            label='Return Component'
            direction='column'
            style={{ width: '350px', maxWidth: '350px' }}
          >
            <Controller
              control={control}
              name={`${fieldName}.returnComponent`}
              render={({ field: { onChange, value } }) => {
                return (
                  <>
                    <ReturnComponentSearch
                      testid={`${fieldName}.component-search`}
                      onChange={(val) => {
                        const value = val ? val.value : val;
                        setValue(`${fieldName}.name`, val.label);
                        onChange(value);
                        trigger(`${fieldName}.returnComponent`);
                      }}
                      onBlur={() => {
                        trigger(`${fieldName}.returnComponent`);
                      }}
                      value={
                        timeWindows[index].returnComponent
                          ? {
                              ...timeWindows[index],
                              label: timeWindows[index].name,
                              value: timeWindows[index].returnComponent,
                            }
                          : ''
                      }
                      hasError={getErrorMessage(`${fieldName}.returnComponent`)}
                      disabled={noReturnComponent}
                      searchInputValue={returnComponentSearch}
                      onSearchInputChange={setReturnComponentSearch}
                    />
                  </>
                );
              }}
            />
            <div style={{ marginTop: 18 }}>
              <Controller
                control={control}
                name={`${fieldName}.noReturnComponent`}
                render={({ field: { onChange, value } }) => (
                  <CheckLabel>
                    <CheckInput
                      {...register}
                      type='checkbox'
                      id={`${fieldName}.noReturnComponent`}
                      readOnly={true}
                      checked={value}
                      onChange={onChange}
                    />
                    <CheckBoxItem htmlFor={`${fieldName}.noReturnComponent`}>
                      <Check />
                    </CheckBoxItem>
                    No additional return component
                  </CheckLabel>
                )}
              />
            </div>
          </TimeWindowControl>
          <TimeWindowControl label='Basis Points Adjustment'>
            <Input
              {...register(`${fieldName}.basisPointAdjustment`)}
              data-testid={`${fieldName}.basisPointAdjustment`}
              placeholder='Enter Basis Points'
              errorMessage={getErrorMessage(
                `${fieldName}.basisPointAdjustment`
              )}
              style={{ width: 200 }}
            />
          </TimeWindowControl>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignSelf: 'center',
          }}
        >
          {hasErrors && <Warning size='13px' className='m-1' fill='#AA0101' />}
          <CircleButton
            key='clone'
            data-testid={`${fieldName}-clone`}
            icon={<Clone size='7px' />}
            title='Clone'
            onClick={() => {
              onClone?.({ ...getValues(fieldName) });
            }}
          />
          <CircleButton
            disabled={timeWindows.length === 1}
            key='delete'
            data-testid={`${fieldName}-delete`}
            icon={<Delete size='7px' />}
            title='Delete'
            onClick={() => {
              onRemove?.(index);
            }}
          />
        </div>
      </TimeWindowHeaderWrapper>
    </div>
  );
};
